// The geometry facts of the touch log (ui-map-interface.md §8 "Touch log",
// R1497, R1498).
//
// The first Android log of R1497 showed the browser deliver every pointer
// event, and still every press on the player's own fleet found no hull. Two
// causes fit that log. The picture on the screen and the hit test can
// disagree (a frame drawn at an old canvas size, a canvas box that is not
// the window), or the finger can simply miss a small hull. This module
// writes the facts that tell the two apart:
//
// - **The press facts.** A `claim none` line and a `tap` or `tap none` line
//   carry the world point of the press, the camera, and the nearest OWN hull
//   with its screen point and its distance from the press in CSS px.
// - **The geometry line.** The window, the visual viewport, the canvas box,
//   the backing store, the surface and what the backend last drew into. It
//   is written at the start of the log and on every resize path, and only
//   when a value changed.
//
// Like the rest of the touch log, it costs nothing while the log is off:
// `watchGeometry` returns before it adds a listener, and a call site builds
// a press text only behind the null test of its hook.

import type { DrawnSize } from './gfx/types';
import { PICK_FLEET_PX } from '../gen/presentation';
import { touchLogSink } from './touchlog';
import { f1 } from '../util/num';

// ---------------------------------------------------------------------------
// The press facts
// ---------------------------------------------------------------------------

/** The part of the view the press text reads. `View` satisfies it. */
export interface ProbeView {
  readonly x: number;
  readonly y: number;
  readonly scale: { readonly x: number };
  /** The arrival's zoom nudge (R838). The picture shows it; the pick does not. */
  readonly nudge?: { readonly k: number };
  screenToWorld(cx: number, cy: number, out: { x: number; y: number }): { x: number; y: number };
}

/** The nearest own hull to a point, as `FleetLayer.nearestOwn` answers it. */
export interface OwnHull {
  id: number;
  /** The smoothed world point, which is the point the pick reads. */
  x: number;
  y: number;
  /** Is the hull under the reveal gate? `pick` skips a faint hull (§5). */
  faint: boolean;
}

/** The one read the press text needs of a fleet layer. */
export interface OwnReader {
  nearestOwn(wx: number, wy: number): OwnHull | null;
}

/** What a page hands its input so the log can state a press. */
export interface PressProbe {
  readonly view: ProbeView;
  /** The fleet layer, or null before the first hello. */
  fleets(): OwnReader | null;
  /**
   * The zoom below which the fleets take no pick, or nothing on a page
   * without that gate. The season map passes `CAMERA_DETAIL_MIN_ZOOM`.
   */
  readonly lodMin?: number;
}

/**
 * The press facts of one screen point, in CSS px, as one text:
 *
 *     at=1234,567 s=0.5000 v=-120.5,33 r=22px own=17@1250,560 scr=505,313 d=9.8px
 *
 * `at` is the world point of the press, `s` the camera scale and `v` the
 * pan. `k` follows when the nudge is not 1, and `lod=off` when the zoom is
 * under the fleet gate. `r` is the pick radius in CSS px. `own` is the
 * nearest own hull at its world point, `scr` its screen point through the
 * same transform the pick inverts, and `d` its distance from the press.
 */
export function pressText(p: PressProbe, cx: number, cy: number): string {
  const v = p.view;
  const s = v.scale.x;
  const w = v.screenToWorld(cx, cy, { x: 0, y: 0 });
  let out = `at=${Math.round(w.x)},${Math.round(w.y)} s=${s.toPrecision(4)} v=${f1(v.x)},${f1(v.y)}`;
  const k = v.nudge?.k ?? 1;
  if (k !== 1) out += ` k=${k.toPrecision(4)}`;
  if (p.lodMin !== undefined && s < p.lodMin) out += ' lod=off';
  out += ` r=${PICK_FLEET_PX}px`;
  const own = p.fleets()?.nearestOwn(w.x, w.y) ?? null;
  if (own === null) return `${out} own=none`;
  // The inverse of `screenToWorld`: the point where the pick expects the
  // hull on the screen.
  const sx = own.x * s + v.x;
  const sy = own.y * s + v.y;
  const dist = Math.hypot(sx - cx, sy - cy);
  return `${out} own=${own.id}@${Math.round(own.x)},${Math.round(own.y)}`
    + ` scr=${Math.round(sx)},${Math.round(sy)} d=${f1(dist)}px${own.faint ? ' faint' : ''}`;
}

/** A hit as `kind:id`, with ` own` for an own fleet, or `none`. */
export function hitText(hit: { kind: string; id: number; own?: boolean } | null): string {
  if (hit === null) return 'none';
  return `${hit.kind}:${hit.id}${hit.own ? ' own' : ''}`;
}

/** What the fleet store holds of a hit fleet, or null when it holds none. */
export interface HitFleet {
  own: boolean;
  faction: number;
  /** The owner's account id, 0 when the frame names no owner. */
  ownerPlayer: number;
}

/**
 * Whose fleet a hit is, as a suffix of a hit text (R1522):
 *
 *     rel=squad owner=4711 me=4700
 *
 * `rel` is `own`, `squad`, `faction` or `foe` from the store's record, or
 * `gone` when the store holds none. `owner` is the owner's account id (`?`
 * when the frame names none) and `me` the player's. A hull the player
 * takes for their own, but the store does not, shows here: its `owner`
 * equals `me`, or its `rel` names a mate.
 */
export function whoseText(
  f: HitFleet | null, me: { playerId: number; faction: number },
  squadmate: (accountId: number) => boolean,
): string {
  if (f === null) return ` rel=gone me=${me.playerId}`;
  const rel = f.own ? 'own'
    : squadmate(f.ownerPlayer) ? 'squad'
      : f.faction === me.faction ? 'faction' : 'foe';
  return ` rel=${rel} owner=${f.ownerPlayer || '?'} me=${me.playerId}`;
}

// ---------------------------------------------------------------------------
// The geometry line
// ---------------------------------------------------------------------------

/** The part of the window the geometry line reads. Every field may be absent. */
export interface GeometryEnv {
  innerWidth?: number;
  innerHeight?: number;
  visualViewport?: {
    width: number; height: number; offsetLeft: number; offsetTop: number; scale: number;
  } | null;
  document?: { documentElement?: { clientHeight?: number } | null };
}

/** The part of the drawing surface the geometry line reads. `Gfx` satisfies it. */
export interface GeometrySource {
  readonly canvas: {
    readonly width: number;
    readonly height: number;
    getBoundingClientRect?(): { x: number; y: number; width: number; height: number };
  };
  /** The surface in CSS px, as `Surface.measure` last stated it. */
  readonly width: number;
  readonly height: number;
  readonly dpr: number;
  drawnSize?(): DrawnSize | null;
}

/**
 * The values of one geometry line, as one text. Every read has its own
 * guard, because an old vendor browser lacks some of the APIs and a read
 * that throws must not lose the line:
 *
 *     inner=360x626 vv=360x800@0,0 x1 docH=626 rect=0,0 360x626 canvas=720x1252 css=360x626 dpr=2 tex(last frame)=720x1252
 */
export function geometryValues(w: GeometryEnv, src: GeometrySource | null): string {
  const out: string[] = [];
  const put = (k: string, read: () => string | undefined) => {
    let v: string | undefined;
    try { v = read(); } catch { v = 'error'; }
    out.push(`${k}=${v ?? '-'}`);
  };
  put('inner', () => (w.innerWidth !== undefined ? `${w.innerWidth}x${w.innerHeight}` : undefined));
  put('vv', () => {
    const v = w.visualViewport;
    if (!v) return undefined;
    return `${f1(v.width)}x${f1(v.height)}@${f1(v.offsetLeft)},${f1(v.offsetTop)} x${v.scale}`;
  });
  put('docH', () => {
    const h = w.document?.documentElement?.clientHeight;
    return h === undefined ? undefined : String(h);
  });
  put('rect', () => {
    const r = src?.canvas.getBoundingClientRect?.();
    return r ? `${f1(r.x)},${f1(r.y)} ${f1(r.width)}x${f1(r.height)}` : undefined;
  });
  put('canvas', () => (src ? `${src.canvas.width}x${src.canvas.height}` : undefined));
  put('css', () => (src ? `${src.width}x${src.height}` : undefined));
  put('dpr', () => (src ? String(src.dpr) : undefined));
  const drawn = (() => { try { return src?.drawnSize?.() ?? null; } catch { return null; } })();
  if (drawn === null) out.push('drawn=-');
  else if (drawn.what === 'tex') {
    // The WebGPU backend records the texture of the last frame it drew; the
    // log never asks the context for a texture outside the frame (R1498).
    out.push(`tex(last frame)=${drawn.w > 0 ? `${drawn.w}x${drawn.h}` : '-'}`);
  } else {
    out.push(`dbuf=${drawn.w}x${drawn.h}`);
  }
  return out.join(' ');
}

/**
 * The thinning of the geometry lines. `check` writes a line only when a
 * value changed since the last line it wrote, so a burst of resize events
 * that changes nothing writes nothing.
 */
export class GeometryLine {
  private last: string | null = null;

  constructor(
    private readonly env: GeometryEnv,
    private readonly src: GeometrySource | null,
    private readonly write: (line: string) => void,
  ) {}

  /** Writes `geom <why> <values>` when a value changed. Answers whether it wrote. */
  check(why: string): boolean {
    const v = geometryValues(this.env, this.src);
    if (v === this.last) return false;
    this.last = v;
    this.write(`geom ${why} ${v}`);
    return true;
  }
}

/** What `watchGeometry` hooks into. `Gfx` satisfies it. */
export interface WatchedSurface extends GeometrySource {
  /** Called after `Surface.measure` resized the backing store. */
  onResize(fn: () => void): void;
  /** Called when the size the backend draws into changed (WebGPU alone). */
  watchDrawn?(fn: () => void): void;
}

/**
 * Starts the geometry lines of a page while the touch log is on: one line
 * now, then one on each `Surface.measure` resize, each window `resize`,
 * each `visualViewport` `resize` and `scroll`, and each change of the
 * texture the WebGPU backend draws into. With the log off it returns at
 * once and adds no listener.
 */
export function watchGeometry(src: WatchedSurface): void {
  const log = touchLogSink();
  if (log === null || typeof window === 'undefined') return;
  const g = new GeometryLine(window as unknown as GeometryEnv, src, log);
  g.check('start');
  src.onResize(() => g.check('measure'));
  src.watchDrawn?.(() => g.check('texture'));
  const opts = { passive: true };
  window.addEventListener('resize', () => g.check('resize'), opts);
  const vv = window.visualViewport;
  if (vv) {
    vv.addEventListener('resize', () => g.check('vv-resize'), opts);
    vv.addEventListener('scroll', () => g.check('vv-scroll'), opts);
  }
}
